import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Mail, Lock, User, AlertCircle, CheckCircle2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await register(form.email, form.password, form.name);
    setLoading(false);
    if (r.ok && r.pending) {
      setPending(true);
    } else if (!r.ok) {
      setError(r.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <aside className="hero-dark relative hidden lg:flex flex-col justify-between p-12 xl:p-16 text-[#F1EDE4]">
        <Logo variant="light" size="lg" />
        <div>
          <h1 className="font-display text-5xl xl:text-6xl leading-[0.98]">
            Únase a un despacho <span className="text-[#C19B6C]">bien llevado.</span>
          </h1>
          <p className="mt-6 max-w-lg text-[#D4C7B8]">
            Configure su cuenta y comience a gestionar clientes, procesos y notarial en minutos.
          </p>
        </div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A7563]">
          © {new Date().getFullYear()} · San José, Costa Rica
        </div>
      </aside>

      <main className="paper flex items-center justify-center p-6 sm:p-10 lg:p-16">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo variant="dark" /></div>

          <h2 className="font-display text-3xl">Crear cuenta</h2>
          <p className="mt-1 text-sm text-[#6B5D57]">Configure el acceso a su despacho</p>

          {pending ? (
            <div className="mt-8 space-y-6" data-testid="register-pending">
              <div className="rounded-lg bg-[#EEF6EE] border border-[#1F5B2E]/25 p-5">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-[#1F5B2E] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                  <div>
                    <div className="font-display text-lg text-[#155724]">Solicitud enviada</div>
                    <p className="mt-1 text-sm text-[#3d5c3d] leading-relaxed">
                      Creamos tu cuenta con el correo <b>{form.email}</b>.
                      Un administrador debe aprobarla antes de que puedas iniciar sesión.
                      Recibirás la confirmación por correo cuando esté activa.
                    </p>
                  </div>
                </div>
              </div>
              <Button
                onClick={() => navigate("/login")}
                data-testid="register-back-to-login"
                className="w-full h-12 bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]"
              >
                Volver a iniciar sesión
              </Button>
            </div>
          ) : (
          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#6B5D57] font-semibold">Nombre completo</label>
              <div className="mt-2 relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B5D57]" strokeWidth={1.8} />
                <Input
                  required data-testid="register-name-input"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="h-12 pl-10 bg-white border-[#E5DFD3] focus-visible:ring-[#7A1E2E]"
                  placeholder="Lic. Andrea Solano"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#6B5D57] font-semibold">Correo electrónico</label>
              <div className="mt-2 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B5D57]" strokeWidth={1.8} />
                <Input
                  type="email" required data-testid="register-email-input"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-12 pl-10 bg-white border-[#E5DFD3] focus-visible:ring-[#7A1E2E]"
                  placeholder="usted@bufete.cr"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#6B5D57] font-semibold">Contraseña</label>
              <div className="mt-2 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B5D57]" strokeWidth={1.8} />
                <Input
                  type="password" required minLength={6}
                  data-testid="register-password-input"
                  value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="h-12 pl-10 bg-white border-[#E5DFD3] focus-visible:ring-[#7A1E2E]"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
            </div>

            {error && (
              <div data-testid="register-error" className="flex items-center gap-2 text-sm text-[#7A1E2E] bg-[#FBEDEF] border border-[#7A1E2E]/25 rounded-md px-3 py-2">
                <AlertCircle className="h-4 w-4" strokeWidth={1.8} /> {error}
              </div>
            )}

            <Button
              type="submit" disabled={loading}
              data-testid="register-submit-button"
              className="w-full h-12 bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear cuenta"}
            </Button>
          </form>
          )}

          {!pending && (
          <p className="mt-8 text-center text-sm text-[#6B5D57]">
            ¿Ya tiene cuenta?{" "}
            <Link to="/login" data-testid="go-to-login-link" className="text-[#C25A5A] hover:text-[#7A1E2E] font-medium">
              Iniciar sesión
            </Link>
          </p>
          )}
        </div>
      </main>
    </div>
  );
}
